import type { Analysis } from './analyze.js';
import type { Severity } from './lint.js';

/**
 * Renders an Analysis as a Markdown document suitable for dropping into a team
 * wiki or onboarding doc. This is the "one-click Markdown export" feature.
 */

const SEVERITY_LABEL: Record<Severity, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

const FORMAT_LABEL: Record<string, string> = {
  sql: 'SQL DDL',
  prisma: 'Prisma schema',
  'json-schema': 'JSON Schema',
};

export function toMarkdown(analysis: Analysis, title = 'Schema Story'): string {
  const { narrative, findings, schema, format } = analysis;
  const lines: string[] = [];

  lines.push(`# ${title}`, '');
  lines.push(`_Generated by Schema Storyteller from a ${FORMAT_LABEL[format] ?? format}._`, '');

  lines.push('## Overview', '', narrative.overview, '');

  lines.push('## Entities', '');
  for (const entity of narrative.entities) {
    lines.push(`### ${entity.name}`, '', entity.summary, '');
    if (entity.relationshipNotes.length > 0) {
      lines.push('**Relationships**', '');
      for (const note of entity.relationshipNotes) lines.push(`- ${note}`);
      lines.push('');
    }
    if (entity.fieldNotes.length > 0) {
      lines.push('**Fields**', '');
      for (const note of entity.fieldNotes) lines.push(`- ${note}`);
      lines.push('');
    }
  }

  lines.push('## Review findings', '');
  if (findings.length === 0) {
    lines.push('No issues detected by the built-in rules. ', '');
  } else {
    lines.push('| Severity | Location | Finding |', '| --- | --- | --- |');
    for (const f of findings) {
      const loc = [f.entity, f.field].filter(Boolean).join('.') || '-';
      lines.push(`| ${SEVERITY_LABEL[f.severity]} | ${escapePipes(loc)} | ${escapePipes(f.message)} |`);
    }
    lines.push('');
  }

  if (schema.warnings.length > 0) {
    lines.push('## Parser notes', '');
    for (const w of schema.warnings) lines.push(`- ${w}`);
    lines.push('');
  }

  return lines.join('\n');
}

function escapePipes(text: string): string {
  return text.replace(/\|/g, '\\|').replace(/\n/g, ' ');
}
